import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

/**
 * ReportGenerationToast
 * ----------------------------------------------------------------------------
 * Bottom-right notification stack for PDF report generation. Three states:
 *   - generating: animated, "{t('report.generatingTitle')}…"
 *   - success:    green check, filename and size, auto-dismisses
 *   - error:      red, with retry hint
 *
 * Lives at z-50 above the loading overlay; dismissible by user.
 */

const formatBytes = (bytes) => {
  if (!bytes) return 'N/A';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const ReportGenerationToast = ({ state, onDismiss }) => {
  const { t } = useTranslation();
  if (state.phase === 'idle') return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 max-w-md animate-in slide-in-from-bottom-4 duration-300">
      {state.phase === 'generating' && (
        <div className="bg-card border border-border rounded-lg shadow-elevated overflow-hidden">
          <div className="flex items-start gap-3 p-4">
            <div className="shrink-0 p-1.5 rounded-md bg-primary/10">
              <Icon name="FileText" size={16} className="text-primary-solid" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <p className="text-sm font-semibold text-foreground">
                  {t('report.generatingTitle')}
                </p>
                <div className="flex items-center gap-0.5">
                  <span className="w-1 h-1 rounded-full bg-primary-solid animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1 h-1 rounded-full bg-primary-solid animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1 h-1 rounded-full bg-primary-solid animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('report.generatingSubtitle')}
              </p>
            </div>
          </div>
          <div className="h-0.5 bg-muted overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-teal-500 to-teal-600"
              style={{
                width: '100%',
                animation: 'progressIndeterminate 1.5s ease-in-out infinite',
              }}
            />
          </div>
        </div>
      )}

      {state.phase === 'success' && (
        <div className="bg-card border border-success/30 rounded-lg shadow-elevated">
          <div className="flex items-start gap-3 p-4">
            <div className="shrink-0 p-1.5 rounded-md bg-success/10">
              <Icon name="CheckCircle2" size={16} className="text-success" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground mb-0.5">
                {t('report.successTitle')}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                <span className="font-mono">{state.filename}</span>
                <span className="ml-2">· {formatBytes(state.sizeBytes)}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={onDismiss}
              className="shrink-0 text-muted-foreground hover:text-foreground transition-smooth"
            >
              <Icon name="X" size={14} />
            </button>
          </div>
        </div>
      )}

      {state.phase === 'error' && (
        <div className="bg-card border border-destructive/30 rounded-lg shadow-elevated">
          <div className="flex items-start gap-3 p-4">
            <div className="shrink-0 p-1.5 rounded-md bg-destructive/10">
              <Icon name="AlertCircle" size={16} className="text-destructive" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground mb-0.5">
                {t('report.errorTitle')}
              </p>
              <p className="text-xs text-muted-foreground">
                {state.error || t('report.errorBody')}
              </p>
            </div>
            <button
              type="button"
              onClick={onDismiss}
              className="shrink-0 text-muted-foreground hover:text-foreground transition-smooth"
            >
              <Icon name="X" size={14} />
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes progressIndeterminate {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

export default ReportGenerationToast;
